import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Auth } from "@aws-amplify/auth";
import { Button, BUTTON_SIZES } from "components/Button";
import AuthInput from "components/AuthInput";
import { Link } from "react-router-dom";
import AuthLayout from "components/AuthLayout";
import { authSubmitButtonStyles, authLinkButtonStyles } from "utils/constants";
import { useAxios } from "../providers/axios";

export default function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAxios();

  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const email = searchParams.get("email");

  const { handleSubmit, handleChange, touched, errors, values } = useFormik({
    initialValues: {
      email: email || "",
      verification_code: ""
    },
    validationSchema: VerifySchema,
    validateOnChange: true,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async ({ email, verification_code }) => {
      try {
        setSubmitting(true);

        const user = await Auth.confirmSignUp(email, verification_code);

        if (user === "SUCCESS") {
          await api.post("/v1/companies/notify-new-company-signup", {
            email
          });
          return navigate(`/login`);
        }
      } catch (error: any) {
        setError(error.message);
      } finally {
        setSubmitting(false);
      }
    }
  });

  const resendCode = async () => {
    try {
      await Auth.resendSignUp(email || "");
    } catch (error: any) {
      setError(error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Pointment | Verify</title>
        <meta property="og:title" content="Pointment | Verify" />
      </Helmet>
      <AuthLayout title="Email verification" error={error} setError={setError}>
        <>
          <p className="mb-6 text-base font-normal font-['Inter']">
            Verification code has been sent to:{" "}
            <span className="font-bold">{email}</span>
          </p>
          <form onSubmit={handleSubmit}>
            <AuthInput
              type="text"
              name="verification_code"
              label="Verification code"
              cyId="verification-code-input"
              placeholder="123456"
              value={values.verification_code}
              onChange={handleChange}
              touched={touched}
              errors={errors}
              autocapitalize="none"
            />
            <div className="flex gap-[16px]">
              <Link
                className="cursor-pointer w-full"
                to={"/register"}
                tabIndex={-1}
              >
                <Button
                  className={authLinkButtonStyles}
                  text={"Return to sign up"}
                  size={BUTTON_SIZES.MEDIUM}
                  type="button"
                  backIcon
                />
              </Link>
              <Button
                className={authSubmitButtonStyles}
                text={"Verify"}
                type="submit"
                cyId="submit-verify-button"
                size={BUTTON_SIZES.MEDIUM}
                submitting={submitting}
                disabledOnSubmit
              />
            </div>
            <div className="h-px w-full mt-8 mb-4 bg-mono-100" />
            <p className="caption text-xs font-['Inter'] font-normal">
              Didn't receive an email or something went wrong? Check your spam
              folder or{" "}
              <button
                className="caption text-[#1C809A] p-0 border-none font-['Inter'] focus:text-blue"
                onClick={resendCode}
                type="button"
              >
                Resend
              </button>
              , this may take a few minutes to arrive.
            </p>
          </form>
        </>
      </AuthLayout>
    </>
  );
}

const VerifySchema = Yup.object().shape({
  email: Yup.string().email().required("Email required"),
  verification_code: Yup.number().required("Verification code required")
});
